import { useState, type ReactNode } from "react";
import { normalizeHex } from "../theme/color";
import { ColorPicker } from "./ColorPicker";
import { HexInput } from "./HexInput";

/**
 * UNE COULEUR, SA PASTILLE, ET SON CODE HEXADÉCIMAL QUI SE TAPE.
 *
 * ⚠️ LA PASTILLE N'EST PLUS UN `input type="color"`. Elle l'a été, et sa fenêtre était
 * celle DU NAVIGATEUR : elle s'ouvrait sur trois cases R, G, B, jamais sur un champ
 * hexadécimal — le chevron qui fait tourner la fenêtre native sur l'hexadécimal est un
 * réglage du navigateur, que rien dans la page ne décide et que tous n'ont pas. Or une
 * charte graphique donne « #91BC8F », pas « 145, 188, 143 ». La pastille ouvre donc
 * maintenant le sélecteur du panneau (`ColorPicker`), où le code hexadécimal est le
 * seul chiffre montré.
 *
 * Le code se tape aussi ICI, dans la ligne, sans rien ouvrir : coller son code de
 * marque reste le geste le plus court. Les règles de la frappe — ce qui part, et
 * quand — vivent dans `HexInput`, partagé avec le sélecteur.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function ColorField({
  value,
  label,
  fallback,
  placeholder,
  onChange,
  children,
}: {
  /** Ce qui est rangé sous la clé — un hexadécimal, ou rien. */
  value: string;
  label: string;
  /** La couleur que montre la pastille tant que rien n'est rangé. */
  fallback: string;
  /** Ce que dit le champ vide : « aucune », « d'après l'image ». */
  placeholder: string;
  onChange: (hex: string) => void;
  /** Ce qui suit le code — le bouton qui rend la couleur automatique, s'il y en a un. */
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  /* ⚠️ NORMALISÉE POUR ÊTRE PEINTE : une valeur reprise de la V1 en majuscules ou sur
     trois chiffres est une couleur que le site affiche bien ; la pastille et le carré
     du sélecteur, eux, travaillent sur `#rrggbb`. */
  const stored = normalizeHex(value);
  const shown = stored ?? fallback;

  return (
    <div className="swatch">
      <button
        type="button"
        className="swatch__dot"
        style={{ background: shown }}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      />
      <HexInput
        className="swatch__hex"
        label={label}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
      />
      {children}
      <ColorPicker
        open={open}
        color={shown}
        value={value}
        label={label}
        placeholder={placeholder}
        onChange={onChange}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
